import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "./api";
import { markInternalDevice } from "./consent";

const AuthCtx = createContext(null);
const KEY = "fbv_token_v1";

export function AuthProvider({ children }) {
  const [token, setToken] = useState(() => localStorage.getItem(KEY));
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      api.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      api.get("/auth/me").then((r) => setUser(r.data)).catch(() => { localStorage.removeItem(KEY); setToken(null); }).finally(() => setLoading(false));
    } else {
      delete api.defaults.headers.common["Authorization"];
      setUser(null); setLoading(false);
    }
  }, [token]);

  // Liefert bei aktivierter Zwei-Faktor-Anmeldung { requires2fa: true, preToken } statt des Nutzers (26.10.2026)
  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    if (data?.requires_2fa) return { requires2fa: true, preToken: data.pre_token };
    api.defaults.headers.common["Authorization"] = `Bearer ${data.token}`;  // sofort setzen: Folgeseiten laden noch im selben Render
    localStorage.setItem(KEY, data.token); setToken(data.token); setUser(data.user);
    return data.user;
  }, []);

  const verify2fa = useCallback(async (preToken, code) => {
    const { data } = await api.post("/auth/2fa/verify", { pre_token: preToken, code });
    api.defaults.headers.common["Authorization"] = `Bearer ${data.token}`;
    localStorage.setItem(KEY, data.token); setToken(data.token); setUser(data.user);
    return data.user;
  }, []);

  const register = useCallback(async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    localStorage.setItem(KEY, data.token); setToken(data.token); setUser(data.user);
    return data.user;
  }, []);

  const logout = useCallback(() => { localStorage.removeItem(KEY); setToken(null); setUser(null); }, []);

  // Team-Geräte aus der Statistik nehmen, sobald sich ein Admin/Mitarbeiter anmeldet
  useEffect(() => { if (user && ["admin", "staff"].includes(user.role)) markInternalDevice(); }, [user]);

  const refresh = useCallback(async () => {
    const { data } = await api.get("/auth/me"); setUser(data); return data;
  }, []);

  return <AuthCtx.Provider value={{ user, token, loading, login, verify2fa, register, logout, refresh }}>{children}</AuthCtx.Provider>;
}

export function useAuth() {
  const c = useContext(AuthCtx);
  if (!c) throw new Error("useAuth outside provider");
  return c;
}
